import { axiosCall, getErrorMessage, isNetworkError, logError } from '../../../ApiClientArchitecture';
import { refreshChatToken, sendMessage, getChat, signInWithGoogle, transcribeSpeech } from '../../../ApiClientArchitecture/apis';
import { isUnauthorizedError } from '../../../ApiClientArchitecture/session';
import { showErrorBanner } from '../../../Components/ErrorBanner';
import { Message, MessageDraft } from '../models/message';
import { ConciergeMessageDraftResponse, ConciergeMessageResponse, ConciergeSendMessageRequest } from '../models/conciergeContracts';
import { ChatHistory, isConflictingClientTurnError, mapApiChatToHistory, mapApiDraftToDraft, mapApiMessageToMessage } from '../mappers/mappers';

/**
 * Outcome of `POST /api/chat`.
 * - `sent`: accepted; the answer arrives over SignalR (and in history).
 * - `in-flight`: a request for the same `clientTurnId` is still running; keep waiting.
 * - `conflict`: the `clientTurnId` was already used with different text; send as a new message.
 * - `failed`: not accepted; safe to retry with the same `clientTurnId`.
 */
export type SendMessageResult =
   | { status: 'sent' }
   | { status: 'in-flight' }
   | { status: 'conflict' }
   | { status: 'failed'; kind: 'network' | 'server'; message?: string; verificationRequired?: boolean };

/**
 * Outcome of "Sign in with Google".
 * - `hotel-not-found`: the link's hotel does not exist; signing in again cannot help, so no banner.
 * - `failed`: rejected for another reason (already reported with a banner).
 */
export type GoogleSignInResult = { status: 'signed-in'; token: string } | { status: 'hotel-not-found' } | { status: 'failed' };

/**
 * Outcome of `POST /api/chat/speech`.
 * - `heard`: the guest's words, to send as their message.
 * - `nothing`: no words were heard.
 * - `failed`: `too-many` (rate limited), `unavailable` (the hotel's daily allowance is used up), else `failed`.
 */
export type TranscribeSpeechResult =
   | { status: 'heard'; text: string }
   | { status: 'nothing' }
   | { status: 'failed'; reason: 'too-many' | 'unavailable' | 'failed' };

/** Error title the API uses for an unknown hotel id. */
const HOTEL_NOT_FOUND = 'HOTEL_NOT_FOUND';
/** Error titles of a refused spoken question: too many in a row, and the hotel's daily allowance used up. */
const RATE_LIMITED = 'RATE_LIMITED';
const BUDGET_EXCEEDED = 'CONCIERGE_BUDGET_EXCEEDED';

export class ChatService {
   /** Chat token for a "Sign in with Google" ID token; the API verifies it. */
   static async signInWithGoogle(data: { hotelId: string; idToken: string }): Promise<GoogleSignInResult> {
      const result = await axiosCall({ endpoint: signInWithGoogle, args: { httpBody: data } });
      if (result.error) {
         logError('ChatService.signInWithGoogle', result.error);
         if (result.error.title === HOTEL_NOT_FOUND) return { status: 'hotel-not-found' };
         showErrorBanner(getErrorMessage(result.error));
         return { status: 'failed' };
      }
      return result.data ? { status: 'signed-in', token: result.data } : { status: 'failed' };
   }

   /** A fresh token for the current (still valid) one; undefined when the session is already over. */
   static async refreshToken(): Promise<string | undefined> {
      const result = await axiosCall({ endpoint: refreshChatToken, args: {} });
      if (result.error) {
         logError('ChatService.refreshToken', result.error);
         return undefined;
      }
      return result.data;
   }

   /**
    * Sends one turn. This is the only code path that calls `POST /api/chat`.
    * `true` = accepted/processed (a ProviderError reply still arrives as an
    * assistant message); `false` or an HTTP error = the request itself failed.
    * Banners are left to the caller, which knows the UI language and whether
    * the turn was already answered.
    */
   static async sendMessage(data: ConciergeSendMessageRequest): Promise<SendMessageResult> {
      // The server contract requires clientTurnId; never send a turn without one.
      if (!data || typeof data.clientTurnId !== 'string' || data.clientTurnId.trim() === '') {
         console.error('[ChatService.sendMessage] refusing to send a message without clientTurnId');
         return { status: 'failed', kind: 'server' };
      }

      const result = await axiosCall<boolean>({ endpoint: sendMessage, args: { httpBody: data } });

      if (!result.error) {
         if (result.data === false) {
            console.warn('[ChatService.sendMessage] server returned false for turn', data.clientTurnId);
            return { status: 'failed', kind: 'server' };
         }
         return { status: 'sent' };
      }

      const error = result.error;
      if (error.type === 'Cancelled') {
         return { status: 'in-flight' };
      }

      logError('ChatService.sendMessage', error);

      if (isConflictingClientTurnError(error)) {
         return { status: 'conflict' };
      }

      if (isNetworkError(error)) {
         return { status: 'failed', kind: 'network' };
      }

      return {
         status: 'failed',
         kind: 'server',
         message: error.message || undefined,
         verificationRequired: error.details?.exceptionType === 'NotCustomerException'
      };
   }

   /**
    * The words of a recorded question. Nothing is sent: the caller sends the words as the guest's message. Banners are
    * left to the caller, which says what happened in the composer.
    */
   static async transcribeSpeech(audio: Blob, languages: string[] = []): Promise<TranscribeSpeechResult> {
      const form = new FormData();
      // The API reads the format from the recording's media type, not its name
      form.append('audio', audio, 'recording');
      // The languages the guest likely speaks, most likely first: a hint for the speech model, which on rough audio can
      // take Greek for another language
      if (languages.length) form.append('languages', languages.join(','));
      const result = await axiosCall({ endpoint: transcribeSpeech, args: { httpBody: form } });

      if (result.error) {
         logError('ChatService.transcribeSpeech', result.error);
         if (result.error.status === 429 || result.error.title === RATE_LIMITED) return { status: 'failed', reason: 'too-many' };
         if (result.error.title === BUDGET_EXCEEDED) return { status: 'failed', reason: 'unavailable' };
         return { status: 'failed', reason: 'failed' };
      }

      const text = result.data?.text?.trim() ?? '';
      return text ? { status: 'heard', text } : { status: 'nothing' };
   }

   /**
    * Retrieves the chat history. Throws on failure so React Query keeps the
    * last good data instead of caching `undefined`.
    */
   static async getChatHistory(options: { silent?: boolean } = {}): Promise<ChatHistory> {
      const result = await axiosCall({ endpoint: getChat });

      if (result.error) {
         logError('ChatService.getChatHistory', result.error);
         if (!options.silent && result.error.type !== 'Cancelled') {
            // An expired session is handled by sending the guest to sign-in, not with an error banner.
            if (!isUnauthorizedError(result.error)) showErrorBanner(getErrorMessage(result.error));
         }
         throw new Error(getErrorMessage(result.error));
      }

      return mapApiChatToHistory(result.data ?? {});
   }

   /**
    * Maps a SignalR MessageResponse to a Message
    */
   static mapSignalRMessageToMessage(response: ConciergeMessageResponse): Message {
      return mapApiMessageToMessage(response);
   }

   /**
    * Maps a SignalR answer draft to a MessageDraft (undefined when it names no turn)
    */
   static mapSignalRDraftToDraft(response: ConciergeMessageDraftResponse): MessageDraft | undefined {
      return mapApiDraftToDraft(response);
   }
}
